import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Condition, ConditionCategory, Document } from '../../types/conditions';
import { StatusBadge } from './StatusBadge';
import { CommentsThread } from './CommentsThread';
import { MessageSquare, Paperclip, Tag, Check, X, AlertCircle, RotateCcw, FolderOpen, FileText, Image as ImageIcon, File as FileIcon, Download, Link2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
interface SiblingCondition {
  id: string;
  title: string;
  category: ConditionCategory;
  status: ConditionStatus;
}
interface ConditionItemProps {
  condition: Condition;
  siblingConditions: SiblingCondition[];
  onApprove: (conditionId: string) => void;
  onReopen: (conditionId: string) => void;
  onReject: (conditionId: string, removedDocIds: string[], reason: string, sendEmail: boolean) => void;
  onAssociateDoc: (docId: string, fromConditionId: string, toConditionId: string, removeFromSource: boolean) => void;
  onMarkRead: (conditionId: string) => void;
}
type DecisionMode = 'idle' | 'rejecting';
function getDocIcon(doc: Document, size: 'sm' | 'lg' = 'sm') {
  const cls = size === 'lg' ? 'h-24 w-24' : 'h-5 w-5';
  switch (doc.fileType) {
    case 'pdf':
      return <FileText className={`${cls} text-red-500`} data-id="element-32" />;
    case 'img':
      return <ImageIcon className={`${cls} text-blue-500`} data-id="element-33" />;
    default:
      return <FileIcon className={`${cls} text-slate-500`} data-id="element-34" />;
  }
}
export function ConditionItem({
  condition,
  siblingConditions,
  onApprove,
  onReopen,
  onReject,
  onAssociateDoc,
  onMarkRead
}: ConditionItemProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeDocId, setActiveDocId] = useState<string | null>(condition.documents[0]?.id ?? null);
  const [decisionMode, setDecisionMode] = useState<DecisionMode>('idle');
  const [docsToRemove, setDocsToRemove] = useState<Set<string>>(new Set());
  const [rejectionReason, setRejectionReason] = useState('');
  const [sendRejectEmail, setSendRejectEmail] = useState(true);
  const [lastRejectEmailSent, setLastRejectEmailSent] = useState<boolean | null>(null);
  const [linkPopoverDocId, setLinkPopoverDocId] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const popoverRef = useRef<HTMLDivElement | null>(null);
  const documents = condition.documents;
  const status = condition.status;
  const hasComments = condition.comments.length > 0;
  const hasDocuments = documents.length > 0;
  const isResolved = status === 'Approved' || status === 'Needs Info';
  // Lock body scroll when modal is open
  useEffect(() => {
    if (isModalOpen) {
      const original = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = original;
      };
    }
  }, [isModalOpen]);
  // Mark all comments on this condition as read when the modal closes
  // (deferred so a comment-only condition doesn't vanish from the queue
  // mid-view the moment the user opens it).
  useEffect(() => {
    if (!isModalOpen) return;
    return () => {
      onMarkRead(condition.id);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isModalOpen]);
  // Keep activeDocId valid as documents list changes
  useEffect(() => {
    if (activeDocId && !documents.find(d => d.id === activeDocId)) {
      setActiveDocId(documents[0]?.id ?? null);
    } else if (!activeDocId && documents.length > 0) {
      setActiveDocId(documents[0].id);
    }
  }, [documents, activeDocId]);
  // Close link popover on outside click
  useEffect(() => {
    if (!linkPopoverDocId) return;
    const handler = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        setLinkPopoverDocId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [linkPopoverDocId]);
  // Auto-dismiss toast
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2800);
    return () => clearTimeout(t);
  }, [toast]);
  const activeDoc = documents.find(d => d.id === activeDocId) ?? null;
  const toggleDocSelected = (id: string) => {
    setDocsToRemove(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);else next.add(id);
      return next;
    });
  };
  const handleApprove = () => {
    onApprove(condition.id);
    setDecisionMode('idle');
  };
  const handleStartReject = () => {
    setDecisionMode('rejecting');
    setDocsToRemove(new Set());
    setRejectionReason('');
    setSendRejectEmail(true);
  };
  const handleConfirmReject = () => {
    onReject(condition.id, Array.from(docsToRemove), rejectionReason, sendRejectEmail);
    setLastRejectEmailSent(sendRejectEmail);
    setDecisionMode('idle');
    setDocsToRemove(new Set());
  };
  const handleCancelReject = () => {
    setDecisionMode('idle');
    setDocsToRemove(new Set());
    setRejectionReason('');
  };
  const handleReopen = () => {
    onReopen(condition.id);
    setDecisionMode('idle');
  };
  const closeModal = () => {
    if (decisionMode === 'rejecting') {
      handleCancelReject();
    }
    setLinkPopoverDocId(null);
    setIsModalOpen(false);
  };
  const openLinkPopover = (docId: string) => {
    setLinkPopoverDocId(cur => cur === docId ? null : docId);
  };
  const handleAssociate = (targetConditionId: string) => {
    if (!linkPopoverDocId) return;
    const doc = documents.find(d => d.id === linkPopoverDocId);
    const target = siblingConditions.find(s => s.id === targetConditionId);
    if (!doc || !target) return;
    onAssociateDoc(linkPopoverDocId, condition.id, targetConditionId, true);
    setToast(`Moved "${doc.filename}" to "${target.title}"`);
    setLinkPopoverDocId(null);
  };
  const removalCount = docsToRemove.size;
  const decisionBanner = useMemo(() => {
    if (status === 'Approved') {
      return {
        bg: 'bg-green-50 border-green-200',
        icon: <Check className="w-4 h-4 text-green-600" data-id="element-35" />,
        text: 'Condition approved',
        textCls: 'text-green-800'
      };
    }
    if (status === 'Needs Info') {
      const text = lastRejectEmailSent === false ? 'Condition rejected — borrower was not emailed' : 'Condition rejected — borrower has been notified';
      return {
        bg: 'bg-red-50 border-red-200',
        icon: <AlertCircle className="w-4 h-4 text-red-600" data-id="element-36" />,
        text,
        textCls: 'text-red-800'
      };
    }
    return null;
  }, [status, lastRejectEmailSent]);
  return <>
      <div className="border border-slate-200 rounded-lg bg-white overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200" data-id="element-37">
        <div className="p-4 flex flex-col sm:flex-row sm:items-center justify-between gap-4" data-id="element-38">
          <div className="flex-1 min-w-0" data-id="element-39">
            <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mb-1" data-id="element-40">
              <h4 className="text-base font-semibold text-slate-900 truncate" data-id="element-41">
                {condition.title}
              </h4>
              <StatusBadge status={status} data-id="element-42" />
            </div>
            <div className="flex items-center flex-wrap text-xs text-slate-500 gap-x-4 gap-y-1" data-id="element-43">
              <span className="flex items-center" data-id="element-44">
                <Tag className="w-3.5 h-3.5 mr-1" data-id="element-45" />
                {condition.category}
              </span>
              {hasComments && <span className="flex items-center font-medium text-slate-700" data-id="element-46">
                  <MessageSquare className="w-3.5 h-3.5 mr-1 text-slate-400" data-id="element-47" />
                  {condition.comments.length}{' '}
                  {condition.comments.length === 1 ? 'Comment' : 'Comments'}
                </span>}
              {hasDocuments && <span className="flex items-center font-medium text-slate-700" data-id="element-48">
                  <Paperclip className="w-3.5 h-3.5 mr-1 text-slate-400" data-id="element-49" />
                  {documents.length}{' '}
                  {documents.length === 1 ? 'Document' : 'Documents'}
                </span>}
            </div>
          </div>

          <div className="flex items-center justify-end flex-shrink-0" data-id="element-50">
            <button onClick={() => setIsModalOpen(true)} className="inline-flex items-center px-4 py-2 text-sm font-medium text-[#0b5cab] bg-white border border-[#0b5cab]/30 rounded-md hover:bg-[#0b5cab]/5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0b5cab] transition-colors" data-id="element-51">
              <FolderOpen className="w-4 h-4 mr-1.5" data-id="element-52" />
              View Documents and Comments
            </button>
          </div>
        </div>
      </div>

      <AnimatePresence data-id="element-53">
        {isModalOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15
      }} className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-sm p-2 sm:p-4" onClick={closeModal} role="dialog" aria-modal="true" aria-label={`${condition.title} details`} data-id="element-54">
            <motion.div initial={{
          opacity: 0,
          scale: 0.97,
          y: 12
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.97,
          y: 12
        }} transition={{
          duration: 0.2
        }} className="bg-white rounded-xl shadow-2xl w-full max-w-7xl h-[94vh] flex flex-col overflow-hidden relative" onClick={e => e.stopPropagation()} data-id="element-55">
              {/* Modal Header */}
              <div className="flex items-start justify-between px-6 py-4 border-b border-slate-200 bg-white" data-id="element-56">
                <div className="min-w-0 flex-1" data-id="element-57">
                  <div className="flex items-center flex-wrap gap-x-3 gap-y-1.5" data-id="element-58">
                    <h2 className="text-lg font-bold text-slate-900 truncate" data-id="element-59">
                      {condition.title}
                    </h2>
                    <StatusBadge status={status} data-id="element-60" />
                  </div>
                  <div className="flex items-center flex-wrap text-xs text-slate-500 gap-x-4 gap-y-1 mt-1.5" data-id="element-61">
                    <span className="flex items-center" data-id="element-62">
                      <Tag className="w-3.5 h-3.5 mr-1" data-id="element-63" />
                      {condition.category}
                    </span>
                    <span className="flex items-center" data-id="element-64">
                      <Paperclip className="w-3.5 h-3.5 mr-1 text-slate-400" data-id="element-65" />
                      {documents.length}{' '}
                      {documents.length === 1 ? 'Document' : 'Documents'}
                    </span>
                    <span className="flex items-center" data-id="element-66">
                      <MessageSquare className="w-3.5 h-3.5 mr-1 text-slate-400" data-id="element-67" />
                      {condition.comments.length}{' '}
                      {condition.comments.length === 1 ? 'Comment' : 'Comments'}
                    </span>
                  </div>
                </div>
                <button onClick={closeModal} className="ml-4 p-1.5 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors flex-shrink-0" aria-label="Close" data-id="element-68">
                  <X className="h-5 w-5" data-id="element-69" />
                </button>
              </div>

              {/* Body: split pane */}
              <div className="flex-1 flex flex-col lg:flex-row min-h-0" data-id="element-70">
                {/* LEFT: Document Preview */}
                <div className="flex-1 flex flex-col bg-slate-200/60 min-h-0 border-b lg:border-b-0 lg:border-r border-slate-200" data-id="element-71">
                  <div className="flex items-center justify-between px-4 py-2.5 bg-slate-800 text-slate-100 text-xs" data-id="element-72">
                    <div className="flex items-center space-x-2 min-w-0" data-id="element-73">
                      {activeDoc ? <>
                          <span className="opacity-80" data-id="element-74">Previewing:</span>
                          <span className="font-medium truncate" data-id="element-75">
                            {activeDoc.filename}
                          </span>
                        </> : <span className="opacity-80" data-id="element-76">No document selected</span>}
                    </div>
                    {activeDoc && <button className="inline-flex items-center px-2 py-1 rounded hover:bg-slate-700 transition-colors" title="Download" data-id="element-77">
                        <Download className="h-3.5 w-3.5 mr-1" data-id="element-78" />
                        Download
                      </button>}
                  </div>

                  <div className="flex-1 overflow-auto p-6 flex items-start justify-center" data-id="element-79">
                    {activeDoc ? <motion.div key={activeDoc.id} initial={{
                  opacity: 0,
                  y: 6
                }} animate={{
                  opacity: 1,
                  y: 0
                }} transition={{
                  duration: 0.18
                }} className="bg-white shadow-xl rounded-md w-full max-w-2xl aspect-[8.5/11] flex flex-col items-center justify-center p-10 border border-slate-300" data-id="element-80">
                        <div className="mb-4" data-id="element-81">
                          {getDocIcon(activeDoc, 'lg')}
                        </div>
                        <p className="text-sm font-medium text-slate-900 text-center break-all" data-id="element-82">
                          {activeDoc.filename}
                        </p>
                        <p className="text-xs text-slate-500 mt-2 text-center" data-id="element-83">
                          Document preview is not available in this demo.
                        </p>
                        <p className="text-xs text-slate-400 mt-1" data-id="element-84">
                          {activeDoc.size} &bull;{' '}
                          {activeDoc.fileType.toUpperCase()} &bull; Uploaded{' '}
                          {new Date(activeDoc.uploadedDate).toLocaleDateString()}
                        </p>
                      </motion.div> : <div className="flex flex-col items-center justify-center text-center text-slate-500 py-16" data-id="element-85">
                        <Paperclip className="h-10 w-10 text-slate-400 mb-3" data-id="element-86" />
                        <p className="text-sm font-medium" data-id="element-87">
                          No documents on this condition
                        </p>
                        <p className="text-xs mt-1" data-id="element-88">
                          The borrower hasn't uploaded any files yet.
                        </p>
                      </div>}
                  </div>
                </div>

                {/* RIGHT: Action panel */}
                <aside className="w-full lg:w-[420px] xl:w-[460px] flex flex-col min-h-0 bg-white" data-id="element-89">
                  {decisionBanner && <div className={`px-5 py-2.5 border-b ${decisionBanner.bg} flex items-center space-x-2`} data-id="element-90">
                      {decisionBanner.icon}
                      <span className={`text-sm font-medium ${decisionBanner.textCls}`} data-id="element-91">
                        {decisionBanner.text}
                      </span>
                    </div>}

                  {/* Decision controls */}
                  <div className="px-5 pt-5 pb-4 border-b border-slate-200" data-id="element-92">
                    <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-3" data-id="element-93">
                      Take Action on Loan Condition
                    </h3>
                    <AnimatePresence mode="wait" data-id="element-94">
                      {decisionMode === 'rejecting' ? <motion.div key="rejecting" initial={{
                    opacity: 0,
                    y: -4
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0,
                    y: -4
                  }} transition={{
                    duration: 0.15
                  }} className="space-y-3" data-id="element-95">
                          <label className="flex items-start gap-2 p-2.5 rounded-md bg-slate-50 border border-slate-200 cursor-pointer select-none" data-id="element-96">
                            <input type="checkbox" checked={sendRejectEmail} onChange={e => setSendRejectEmail(e.target.checked)} className="h-4 w-4 mt-0.5 rounded border-slate-300 text-[#0b5cab] focus:ring-[#0b5cab] flex-shrink-0" data-id="element-97" />
                            <div className="text-xs" data-id="element-98">
                              <p className="font-medium text-slate-900" data-id="element-99">
                                Email the borrower
                              </p>
                              <p className="text-slate-500 mt-0.5" data-id="element-100">
                                {sendRejectEmail ? 'They will receive an email with the rejection reason.' : 'No email will be sent. You can notify them another way.'}
                              </p>
                            </div>
                          </label>
                          <div data-id="element-101">
                            <label className="block text-xs font-medium text-slate-700 mb-1.5" data-id="element-102">
                              Reason for rejection
                              {sendRejectEmail ? <span className="text-red-600 ml-1" data-id="element-103">*</span> : <span className="text-slate-400 font-normal ml-1" data-id="element-104">
                                  (optional)
                                </span>}
                            </label>
                            <textarea rows={3} value={rejectionReason} onChange={e => setRejectionReason(e.target.value)} placeholder={sendRejectEmail ? 'Required — this message will be emailed to the borrower.' : "Let the borrower know what's needed to resolve this condition..."} className="block w-full rounded-md border border-slate-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm p-2.5 resize-none" data-id="element-105" />
                            {sendRejectEmail && <p className="mt-1 text-[11px] text-slate-500" data-id="element-106">
                                A reason is required when emailing the borrower.
                              </p>}
                          </div>
                          <p className="text-xs text-slate-600" data-id="element-107">
                            {removalCount > 0 ? <>
                                <span className="font-semibold text-red-700" data-id="element-108">
                                  {removalCount}
                                </span>{' '}
                                document{removalCount !== 1 ? 's' : ''} will be
                                un-associated.
                              </> : 'Tip: Use the checkbox on any document below to un-associate it.'}
                          </p>
                          <div className="grid grid-cols-2 gap-2" data-id="element-109">
                            <button onClick={handleCancelReject} className="px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 transition-colors" data-id="element-110">
                              Cancel
                            </button>
                            <button onClick={handleConfirmReject} disabled={sendRejectEmail && !rejectionReason.trim()} className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-md hover:bg-red-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors" data-id="element-111">
                              <X className="w-4 h-4 mr-1.5" data-id="element-112" />
                              Confirm Reject
                            </button>
                          </div>
                        </motion.div> : isResolved ? <motion.div key="resolved" initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} exit={{
                    opacity: 0
                  }} transition={{
                    duration: 0.15
                  }} data-id="element-113">
                          <button onClick={handleReopen} className="w-full inline-flex items-center justify-center px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 transition-colors" data-id="element-114">
                            <RotateCcw className="w-4 h-4 mr-1.5" data-id="element-115" />
                            Reopen Condition
                          </button>
                        </motion.div> : <motion.div key="idle" initial={{
                    opacity: 0
                  }} animate={{
                    opacity: 1
                  }} exit={{
                    opacity: 0
                  }} transition={{
                    duration: 0.15
                  }} className="space-y-2" data-id="element-116">
                          {!hasDocuments && <p className="text-[11px] text-slate-500 leading-relaxed" data-id="element-117">
                              No documents attached. Mark as reviewed to clear
                              this condition from your queue.
                            </p>}
                          <div className="grid grid-cols-2 gap-2" data-id="element-118">
                            {hasDocuments ? <button onClick={handleApprove} className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-white bg-[#0b5cab] border border-transparent rounded-md hover:bg-[#094a89] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0b5cab] transition-colors" data-id="element-119">
                                <Check className="w-4 h-4 mr-1.5" data-id="element-120" />
                                Approve
                              </button> : <button onClick={closeModal} className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-white bg-[#0b5cab] border border-transparent rounded-md hover:bg-[#094a89] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0b5cab] transition-colors" data-id="element-121">
                                <Check className="w-4 h-4 mr-1.5" data-id="element-122" />
                                Mark as Reviewed
                              </button>}
                            <button onClick={handleStartReject} className="inline-flex items-center justify-center px-3 py-2 text-sm font-medium text-red-700 bg-white border border-red-300 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors" data-id="element-123">
                              <X className="w-4 h-4 mr-1.5" data-id="element-124" />
                              Reject
                            </button>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>

                  {/* Right pane scrollable content */}
                  <div className="flex-1 overflow-y-auto" data-id="element-125">
                    {/* Documents list */}
                    <div className="px-5 py-4 border-b border-slate-200" data-id="element-126">
                      <div className="flex items-center justify-between mb-3" data-id="element-127">
                        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center" data-id="element-128">
                          <Paperclip className="w-3.5 h-3.5 mr-1.5" data-id="element-129" />
                          Documents ({documents.length})
                        </h3>
                        {decisionMode === 'rejecting' && <span className="text-[10px] uppercase tracking-wide font-semibold text-red-700" data-id="element-130">
                            Select to un-associate
                          </span>}
                      </div>

                      {hasDocuments ? <ul className="space-y-1.5" data-id="element-131">
                          {documents.map(doc => {
                      const isActive = doc.id === activeDocId;
                      const isMarked = docsToRemove.has(doc.id);
                      const isRejecting = decisionMode === 'rejecting';
                      const popoverOpen = linkPopoverDocId === doc.id;
                      return <li key={doc.id} className="relative" data-id="element-132">
                                <div className={`flex items-center gap-2 p-2 rounded-md border transition-colors cursor-pointer ${isMarked ? 'border-red-300 bg-red-50' : isActive ? 'border-[#0b5cab] bg-[#0b5cab]/5' : 'border-slate-200 bg-white hover:bg-slate-50'}`} onClick={() => setActiveDocId(doc.id)} data-id="element-133">
                                  {isRejecting && <input type="checkbox" checked={isMarked} onChange={() => toggleDocSelected(doc.id)} onClick={e => e.stopPropagation()} className="h-4 w-4 rounded border-slate-300 text-red-600 focus:ring-red-500 flex-shrink-0" aria-label={`Un-associate ${doc.filename}`} data-id="element-134" />}
                                  <div className="flex-shrink-0" data-id="element-135">
                                    {getDocIcon(doc)}
                                  </div>
                                  <div className="min-w-0 flex-1" data-id="element-136">
                                    <p className={`text-sm font-medium truncate ${isActive ? 'text-[#0b5cab]' : 'text-slate-900'}`} data-id="element-137">
                                      {doc.filename}
                                    </p>
                                    <p className="text-[11px] text-slate-500 truncate" data-id="element-138">
                                      {doc.size} &bull; {doc.uploadedBy}
                                    </p>
                                  </div>

                                  {!isRejecting && siblingConditions.length > 0 && <button onClick={e => {
                            e.stopPropagation();
                            openLinkPopover(doc.id);
                          }} className={`flex-shrink-0 p-1.5 rounded transition-colors ${popoverOpen ? 'bg-[#0b5cab] text-white' : 'text-slate-400 hover:text-[#0b5cab] hover:bg-slate-100'}`} title="Associate with another condition" aria-label="Associate with another condition" data-id="element-139">
                                        <Link2 className="h-4 w-4" data-id="element-140" />
                                      </button>}
                                </div>

                                {/* Associate popover */}
                                <AnimatePresence data-id="element-141">
                                  {popoverOpen && <motion.div ref={popoverRef} initial={{
                            opacity: 0,
                            y: -4
                          }} animate={{
                            opacity: 1,
                            y: 0
                          }} exit={{
                            opacity: 0,
                            y: -4
                          }} transition={{
                            duration: 0.12
                          }} className="absolute right-0 top-full mt-1.5 w-72 bg-white border border-slate-200 rounded-lg shadow-xl z-20 overflow-hidden" onClick={e => e.stopPropagation()} data-id="element-142">
                                      <div className="px-3 py-2.5 border-b border-slate-200 bg-slate-50" data-id="element-143">
                                        <p className="text-xs font-semibold text-slate-900" data-id="element-144">
                                          Move document to…
                                        </p>
                                        <p className="text-[11px] text-slate-500 truncate mt-0.5" data-id="element-145">
                                          {doc.filename}
                                        </p>
                                      </div>
                                      <ul className="max-h-56 overflow-y-auto py-1" data-id="element-146">
                                        {siblingConditions.map(sib => {
                                const statusDot = sib.status === 'Approved' ? 'bg-green-500' : sib.status === 'Needs Info' ? 'bg-red-500' : sib.status === 'Requested' ? 'bg-slate-400' : 'bg-amber-500';
                                return <li key={sib.id} data-id="element-147">
                                              <button onClick={() => handleAssociate(sib.id)} className="w-full text-left px-3 py-2 hover:bg-slate-50 transition-colors flex items-start gap-2" data-id="element-148">
                                                <Link2 className="w-3.5 h-3.5 text-slate-400 mt-0.5 flex-shrink-0" data-id="element-149" />
                                                <div className="min-w-0 flex-1" data-id="element-150">
                                                  <p className="text-sm text-slate-900 truncate" data-id="element-151">
                                                    {sib.title}
                                                  </p>
                                                  <div className="flex items-center gap-1.5 mt-0.5" data-id="element-152">
                                                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${statusDot}`} aria-hidden="true" data-id="element-153" />
                                                    <p className="text-[11px] text-slate-500 truncate" data-id="element-154">
                                                      {sib.category} &bull;{' '}
                                                      {sib.status}
                                                    </p>
                                                  </div>
                                                </div>
                                              </button>
                                            </li>;
                              })}
                                      </ul>
                                    </motion.div>}
                                </AnimatePresence>
                              </li>;
                    })}
                        </ul> : <div className="p-3 border border-dashed border-slate-300 rounded-md text-center bg-slate-50" data-id="element-155">
                          <p className="text-xs text-slate-500" data-id="element-156">
                            No documents uploaded yet.
                          </p>
                        </div>}
                    </div>

                    {/* Comments */}
                    <div className="px-5 py-4" data-id="element-157">
                      <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-500 flex items-center mb-3" data-id="element-158">
                        <MessageSquare className="w-3.5 h-3.5 mr-1.5" data-id="element-159" />
                        Conversation ({condition.comments.length})
                      </h3>
                      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3" data-id="element-160">
                        <CommentsThread comments={condition.comments} data-id="element-161" />
                      </div>
                    </div>
                  </div>
                </aside>
              </div>

              {/* Toast */}
              <AnimatePresence data-id="element-162">
                {toast && <motion.div initial={{
              opacity: 0,
              y: 8
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              y: 8
            }} transition={{
              duration: 0.15
            }} className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-slate-900 text-white text-sm px-4 py-2 rounded-lg shadow-xl flex items-center gap-2 z-30" data-id="element-163">
                    <Check className="w-4 h-4 text-green-400" data-id="element-164" />
                    {toast}
                  </motion.div>}
              </AnimatePresence>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}